// SPDX-FileCopyrightText: Copyright (C) 2026 provide.io llc
// SPDX-License-Identifier: Apache-2.0

// biome-ignore-all lint/style/useNamingConvention: react-router lazy() API
// requires the returned key be `Component` (PascalCase).
import type { ComponentType } from "react";

// When the SPA is reloaded between deploys, `index.html` references new
// hashed chunk filenames but the user's open tab still has the old ones.
// Lazy `import("...")` of a deleted chunk path falls through to the SPA
// fallback (which returns `index.html` as `text/html`) — React Router then
// throws "'text/html' is not a valid JavaScript MIME type".
//
// We detect that specific failure shape and force a one-shot hard reload so
// the user picks up the fresh manifest. The `?_reload=` marker stops us
// from looping when the underlying problem is something else (a non-cache
// chunk-load failure should surface, not silently reload forever).
const RELOAD_MARKER = "uwhois.lazyReloadAt";

function isStaleChunkError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  const m = err.message ?? "";
  return (
    m.includes("text/html") ||
    m.includes("Failed to fetch dynamically imported module") ||
    m.includes("error loading dynamically imported module")
  );
}

function shouldReload(): boolean {
  try {
    const last = window.sessionStorage.getItem(RELOAD_MARKER);
    if (!last) return true;
    // Only reload at most once per 30s — if a real chunk really is missing
    // we surface the error rather than loop.
    return Date.now() - Number(last) > 30_000;
  } catch {
    /* v8 ignore next */
    return true;
  }
}

function markReloaded(): void {
  try {
    window.sessionStorage.setItem(RELOAD_MARKER, String(Date.now()));
  } catch {
    // sessionStorage unavailable — fall through to hard reload anyway.
    /* v8 ignore next */
  }
}

// biome-ignore lint/suspicious/noExplicitAny: ComponentType prop variance is invariant under strict mode; route modules export components with disparate prop signatures that all collapse to "no usable external props" at the router boundary
type AnyComponent = ComponentType<any>;

export async function loadRouteComponent<M extends Readonly<Record<string, AnyComponent>>>(
  importer: () => Promise<M>,
  pick: (mod: M) => AnyComponent | undefined,
): Promise<{ Component: AnyComponent }> {
  try {
    const mod = await importer();
    const Component = pick(mod);
    if (!Component) {
      // ``noUncheckedIndexedAccess`` widens index-signature lookups to
      // ``T | undefined``. In practice the named export always exists at
      // runtime (every route file exports its component), but TypeScript
      // can't know that — narrow here so the caller gets a strict type.
      throw new Error("loadRouteComponent: picked export is undefined");
    }
    return { Component };
  } catch (err: unknown) {
    if (isStaleChunkError(err) && shouldReload()) {
      markReloaded();
      window.location.reload();
      // Return a never-resolving promise so React Router doesn't render
      // anything before the reload kicks in.
      return new Promise<{ Component: AnyComponent }>(() => undefined);
    }
    throw err;
  }
}
